import React, { Fragment, useEffect, useRef } from 'react';
import { Col, DatePicker, Form, Row, Select } from 'antd';
import InputLabel from 'components/InputLabel/index';
import dayjs from 'dayjs';
import CardAccordion from '../CardAccordion';

import { constantFormValues } from '../../root';
import { useControlData } from '../LeaseUtils/helper';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { useLocation } from 'react-router-dom';
import { glCodes } from '../GLCodes';

const AccountingInformation = ({ screen, identityId }) => {
	const form = Form.useFormInstance();
	const { state } = useLocation();

	const [getAccountingAssignment] = useSendControlDataMutation();

	const lastPayloadRef = useRef(null);

	function formatAccountingAssignmentData(inputData) {
		// Parse JSON if input is a string
		const data = typeof inputData === 'string' ? JSON.parse(inputData) : inputData;
		// Create output object
		const formattedOutput = {};
		// Process each key in the input data
		for (const key in data) {
			const entry = data[key];
			// Skip null entries
			if (entry === null) continue;

			// If the key is 'securityDeposit', use 'securityDepositName' as the output key
			if (key === 'securityDeposit') {
				formattedOutput['securityDepositName'] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			} else {
				formattedOutput[key] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			}
		}
		return formattedOutput;
	}
	const setAccountingAssignmentData = async (payload) => {
		// Skip API call if payload is missing data or data is null/undefined
		if (!payload || payload.data == null) {
			return;
		}
		// Compare new payload with the last one
		if (JSON.stringify(lastPayloadRef.current) === JSON.stringify(payload)) {
			// Payload is the same, skip API call
			return;
		}
		lastPayloadRef.current = payload; // Update last payload

		await getAccountingAssignment(payload).then((res) => {
			form.setFieldsValue({
				...formatAccountingAssignmentData(res?.data?.responseData),
			});
		});
	};
	useEffect(() => {
		if (screen?.toLowerCase() !== 'create' && identityId) {
			const payload = {
				data: identityId,
				processCode: state?.processCode,
				scriptName: 'lease-controller-accounts',
			};
			setAccountingAssignmentData(payload);
		}
	}, [identityId]);

	const { lynFormProperties } = useControlData('10072');

	return (
		<>
			<CardAccordion
				title="Reporting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<Row gutter={[16, 0]}>
					<Col xs={4}>
						<InputLabel label={constantFormValues?.endOfFinancialYear?.label} head={true} />
					</Col>
					<Col xs={8}>
						<Form.Item
							name={constantFormValues?.endOfFinancialYear?.key}
							initialValue={dayjs().month(11).date(31)} // December 31st
							rules={[
								{
									required: false,
								},
							]}
						>
							<DatePicker
								allowClear={false}
								disabled={screen === 'VIEW'}
								format="DD MMMM" // Display format shows only month and day
								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								picker="date" // Still use date picker
								disabledDate={() => false} // Don't disable any dates based on year
								onChange={(date) => {
									// When a date is selected, preserve only month and day
									// and use current year
									if (date) {
										const currentYear = dayjs().year();
										const newDate = date.year(currentYear);
										form.setFieldsValue({
											[constantFormValues?.endOfFinancialYear?.key]: newDate,
										});
									}
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
			</CardAccordion>

			<CardAccordion
				title="Accounting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<Row gutter={[16, 0]}>
					{Object.entries(glCodes).map(([label, key], index) => (
						<Fragment key={index}>
							<Col xs={4}>
								<InputLabel label={label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={`${key}Id`} noStyle />
								<Form.Item name={`${key}Type`} noStyle />
								<Form.Item name={`${key}Description`} noStyle />
								<Form.Item
									name={key}
									rules={[
										{
											required: false,
											// message: 'This filed is required',
										},
									]}
								>
									<Select
										showSearch
										allowClear
										disabled
										style={{ width: '100%', height: '32px' }}
										filterOption={true}
										placeholder="Select from the list"
										onSelect={(value, options) => {
											form.setFieldValue(`${key}Id`, options.key);

											const selectedOption = lynFormProperties?.[key].find((item) => item?.generalLedgerCode === value);

											if (selectedOption?.generalLegerDescription) {
												const concatLabel = `${value}-${selectedOption.generalLegerDescription}`;
												form.setFieldValue(`${key}Description`, concatLabel);
												form.setFieldValue(`${key}Type`, selectedOption?.accountType);
											}
										}}
									>
										{lynFormProperties?.[key]?.map(({ generalLedgerCode, generalLegerDescription, id }) => (
											<Select.Option key={id} value={generalLedgerCode}>
												{generalLedgerCode} - {generalLegerDescription}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Fragment>
					))}
				</Row>
			</CardAccordion>
		</>
	);
};

export default AccountingInformation;
